import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';

import UserSearchWidget, { SearchResult } from '~/components/Common/UserSearchWidget';

import { useCreditPartnerEarn } from '~/hooks/apis/loyalty/useCreditPartnerEarn';
import { useGetLoyaltyAccount } from '~/hooks/apis/loyalty/useGetLoyaltyAccount';

import UsersService from '~/services/UsersService';

import ApiError from '~/types/ApiError';
import type { CppPartner } from '~/types/costPerPoint';
import type { PartnerEarnBulkResult } from '~/types/loyaltyContract';

import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';
import { formatDecimal } from '~/utils/numberFormatters';

import {
  EMPTY_PARTNER_EARN_CREDIT_VALUES,
  type PartnerEarnCreditValues,
  buildPartnerEarnCreditBody,
  partnerLimitsLabel,
  validatePartnerEarnCredit,
} from '../partnerEarnCredit';

interface Props {
  partner: CppPartner;
  onBack: () => void;
  onSubmitSuccess: (response: PartnerEarnBulkResult) => void;
}

interface SelectedCustomer {
  id: string;
  fullName: string;
  email: string;
}

const CUSTOMER_SEARCH_FIELD_ID = 'partner-earn-customer-search';
const CUSTOMER_SEARCH_LABEL = 'Name / email / phone / Société account / user ID';

function isLoyaltyAccountNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export default function ManualCreditStep({ partner, onBack, onSubmitSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [userSearchLabel, setUserSearchLabel] = useState('');
  const [values, setValues] = useState<PartnerEarnCreditValues>(EMPTY_PARTNER_EARN_CREDIT_VALUES);

  const accountQuery = useGetLoyaltyAccount(selectedCustomer?.id, !!selectedCustomer);
  const isNotEnrolled = accountQuery.isError && isLoyaltyAccountNotFound(accountQuery.error);
  const accountLookupFailed = accountQuery.isError && !isNotEnrolled;
  const accountSummary = isNotEnrolled ? null : (accountQuery.data?.result?.accountSummary ?? null);
  const accountNumber = accountSummary?.accountNumber;

  const errors = useMemo(() => validatePartnerEarnCredit(values, partner), [values, partner]);
  const hasFieldErrors = Object.keys(errors).some((key) => key !== 'form');

  const creditMutation = useCreditPartnerEarn({
    onSuccess: (data) => {
      enqueueSnackbar(
        data.alreadyCredited
          ? `Already credited as ${partner.name} — no new points added`
          : `Partner earn queued as ${partner.name}`,
        { variant: data.alreadyCredited ? 'info' : 'success' },
      );
      onSubmitSuccess({
        totalProcessed: 1,
        successCount: data.alreadyCredited ? 0 : 1,
        alreadyCreditedCount: data.alreadyCredited ? 1 : 0,
        failedCount: 0,
        results: [
          {
            accountNumber: data.accountNumber,
            success: true,
            alreadyCredited: data.alreadyCredited,
            idempotencyKey: data.idempotencyKey,
            transactionId: data.transactionId,
          },
        ],
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to queue partner earn: ${error.message}`, { variant: 'error' });
    },
  });

  const canSubmit =
    !!selectedCustomer &&
    typeof accountNumber === 'number' &&
    !hasFieldErrors &&
    !errors.form &&
    !creditMutation.isPending &&
    !!values.activityTitle.trim() &&
    !!values.reason.trim();

  const resetCreditForm = useCallback(() => {
    setValues(EMPTY_PARTNER_EARN_CREDIT_VALUES);
  }, []);

  const setFieldValue = (key: keyof PartnerEarnCreditValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSelectResult = useCallback(
    (result: SearchResult | null) => {
      resetCreditForm();
      if (!result || result.entityType !== 'user') {
        setSelectedCustomer(null);
        return;
      }

      const customer = {
        id: result.value,
        fullName: result.fullName?.trim() || '',
        email: result.email,
      };
      setSelectedCustomer(customer);
      setUserSearchLabel(customer.email);

      if (customer.fullName) {
        return;
      }

      void UsersService.getUser(result.value, { brand })
        .then((user) => {
          const fullName = [user.givenName, user.surname].filter(Boolean).join(' ').trim() || user.fullName?.trim();
          if (!fullName) {
            return;
          }
          setSelectedCustomer((prev) => (prev?.id === result.value ? { ...prev, fullName } : prev));
        })
        .catch(() => undefined);
    },
    [brand, resetCreditForm],
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setUserSearchLabel(value);
      if (selectedCustomer && value.trim() !== selectedCustomer.email.trim()) {
        setSelectedCustomer(null);
        resetCreditForm();
      }
    },
    [resetCreditForm, selectedCustomer],
  );

  const handleSearchChange = useCallback(
    (_fieldId: string, value: string) => {
      if (!value) {
        setSelectedCustomer(null);
        resetCreditForm();
      }
    },
    [resetCreditForm],
  );

  const handleSubmit = () => {
    if (typeof accountNumber !== 'number') {
      return;
    }
    const credit = buildPartnerEarnCreditBody(accountNumber, values, partner);
    if (!credit) {
      return;
    }
    creditMutation.mutate({ partnerId: partner.id, credit });
  };

  const pointsHelper =
    errors.basePoints ??
    `Must be between ${formatDecimal(partner.minPointsPerTransaction)} and ${formatDecimal(partner.maxPointsPerTransaction)}`;
  const statusHelper =
    errors.statusCredits ??
    `Must be between ${formatDecimal(partner.minStatusCreditsPerTransaction)} and ${formatDecimal(partner.maxStatusCreditsPerTransaction)}`;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Manual partner credit
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Search for a customer, then credit base points{partner.allowCreditStatusCredits ? ' or status credits' : ''} as{' '}
        {partner.name} ({partnerLimitsLabel(partner)}).
      </Typography>

      <Box mb={3}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Customer
        </Typography>
        <UserSearchWidget
          id={CUSTOMER_SEARCH_FIELD_ID}
          label={CUSTOMER_SEARCH_LABEL}
          formErrors={[]}
          loading={false}
          onChange={handleSearchChange}
          onSearchInputChange={handleSearchInputChange}
          onSelectResult={handleSelectResult}
          passedAutocompleteValue={userSearchLabel}
          setPassedAutocompleteValue={setUserSearchLabel}
        />
      </Box>

      {selectedCustomer && accountQuery.isLoading && (
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking Société membership…
          </Typography>
        </Stack>
      )}

      {selectedCustomer && accountLookupFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load this customer’s Société account. Try again or open them from the user search on the home page.
        </Alert>
      )}

      {selectedCustomer && isNotEnrolled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {selectedCustomer.fullName || selectedCustomer.email} has no Société account, so they cannot be credited.
        </Alert>
      )}

      {selectedCustomer && accountSummary && typeof accountNumber !== 'number' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This Société account has no membership number, so it cannot be credited.
        </Alert>
      )}

      {selectedCustomer && accountSummary && typeof accountNumber === 'number' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Member
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 1.5,
              }}
            >
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  Name
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={0.5}>
                  {selectedCustomer.fullName || selectedCustomer.email}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  Membership
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={0.5}>
                  {accountNumber}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  Tier
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={0.5}>
                  {getLuxLoyaltyTierDisplayName(accountSummary.tier)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  Points
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={0.5}>
                  {formatDecimal(accountSummary.points)}
                </Typography>
              </Paper>
            </Box>
          </Box>

          {errors.form && <Alert severity="info">{errors.form}</Alert>}

          <TextField
            label="Base points"
            type="number"
            value={values.basePoints}
            onChange={(event) => setFieldValue('basePoints', event.target.value)}
            error={!!errors.basePoints}
            helperText={pointsHelper}
            slotProps={{
              htmlInput: {
                min: partner.minPointsPerTransaction,
                max: partner.maxPointsPerTransaction,
                step: 1,
              },
            }}
          />

          {partner.allowCreditStatusCredits && (
            <TextField
              label="Status credits"
              type="number"
              value={values.statusCredits}
              onChange={(event) => setFieldValue('statusCredits', event.target.value)}
              error={!!errors.statusCredits}
              helperText={statusHelper}
              slotProps={{
                htmlInput: {
                  min: partner.minStatusCreditsPerTransaction,
                  max: partner.maxStatusCreditsPerTransaction,
                  step: 1,
                },
              }}
            />
          )}

          <TextField
            label="Activity title"
            value={values.activityTitle}
            onChange={(event) => setFieldValue('activityTitle', event.target.value)}
            error={!!errors.activityTitle}
            helperText={errors.activityTitle ?? 'Shown on the customer activity'}
          />

          <TextField
            label="Reason"
            value={values.reason}
            onChange={(event) => setFieldValue('reason', event.target.value)}
            error={!!errors.reason}
            helperText={errors.reason ?? 'Audit reason for this credit'}
            multiline
            minRows={2}
          />
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" mt={4}>
        <Button variant="text" onClick={onBack} disabled={creditMutation.isPending}>
          Back
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {creditMutation.isPending ? 'Submitting...' : 'Credit as partner'}
        </Button>
      </Stack>
    </Box>
  );
}
